import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeftIcon } from '../icons/ArrowLeftIcon';
import { PlusIcon } from '../icons/PlusIcon';
import { TrashIcon } from '../icons/TrashIcon';
import { MicrophoneIcon } from '../icons/MicrophoneIcon';
import { UploadIcon } from '../icons/UploadIcon';
import { MagicWandIcon } from '../icons/MagicWandIcon';
import { LoadingSpinner } from '../icons/LoadingSpinner';
import { CharacterPortraitModal } from '../CharacterPortraitModal';
import { UserAssetsModal } from './UserAssetsModal';
import { ActorsLibraryModal } from './ActorsLibraryModal';
import { Wand2, Upload, FolderOpen, Users, Check, Pencil } from 'lucide-react';
import { ReferenceImageStrip } from './ReferenceImageStrip';
import { CostumeEditModal } from './CostumeEditModal';
import { cloneVoiceWithElevenLabs } from '../../services/elevenlabsService';
import { deleteClonedVoice } from '../../services/aiService';
import { ElevenLabsVoiceModal } from '../ElevenLabsVoiceModal';
import { VoiceClonerModal } from '../VoiceClonerModal';
import { cropImageToAspectRatio } from '../../services/geminiService';
import { generateImage, generateElementImage, generateCharacterPortrait } from '../../services/aiService';
import { generateCharacterPortraitWithGemini } from '../../services/geminiService';
import { buildCharacterSheetPrompt } from '../../utils/characterSheetPrompt';
import { buildCostumePrompt } from '../../utils/costumePrompt';
import { fetchImageViaProxy, uploadDataUriToBackend, isAlreadyPersisted } from '../../services/backendService';
import { getCharacterCostumeForVideo } from '../../services/videoPayloadBuilder';
import { CREDIT_COSTS } from '../../types';

// Internal helper: try-on selection modal
const TryOnConfirmModal = ({ characters, onResolve }) => {
    const [selectedIds, setSelectedIds] = useState(() => new Set(characters.map(c => c.id)));
    const allSelected = selectedIds.size === characters.length;
    const noneSelected = selectedIds.size === 0;

    const toggle = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        setSelectedIds(allSelected ? new Set() : new Set(characters.map(c => c.id)));
    };

    return (
        <div
            className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
            data-testid="try-on-confirm-modal"
        >
            <div className="relative max-w-md w-full bg-zinc-900 border border-violet-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                <div className="p-6 pb-4">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-violet-500/20 border border-violet-500/40 flex items-center justify-center">
                            <svg className="w-5 h-5 text-violet-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-base font-semibold text-white">Run Virtual Try-On?</h3>
                            <p className="text-xs text-zinc-400 mt-0.5">
                                {selectedIds.size} of {characters.length} selected
                            </p>
                        </div>
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed mb-1">
                        Try-on combines each character's portrait with their costume into a unified image used across the storyboard.
                    </p>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                        <span className="text-violet-300 font-medium">Uncheck</span> characters that already look correct in their portrait — saves credits.
                    </p>
                </div>
                {/* Character list */}
                <div className="px-6 pb-2 flex-1 overflow-y-auto">
                    <div className="flex items-center justify-between mb-2">
                        <button
                            type="button"
                            onClick={toggleAll}
                            data-testid="try-on-toggle-all-btn"
                            className="text-xs text-violet-300 hover:text-violet-200 font-medium"
                        >
                            {allSelected ? 'Deselect all' : 'Select all'}
                        </button>
                    </div>
                    <ul className="space-y-1.5">
                        {characters.map(char => {
                            const checked = selectedIds.has(char.id);
                            return (
                                <li key={char.id}>
                                    <label
                                        className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                                            checked
                                                ? 'bg-violet-500/10 border-violet-500/30'
                                                : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                                        }`}
                                        data-testid={`try-on-char-${char.id}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggle(char.id)}
                                            className="accent-violet-500 w-4 h-4"
                                            data-testid={`try-on-char-checkbox-${char.id}`}
                                        />
                                        {char.image && (
                                            <img
                                                src={char.image}
                                                alt={char.name}
                                                className="w-9 h-9 rounded-md object-cover flex-shrink-0"
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-zinc-100 font-medium truncate">{char.name}</p>
                                            <p className="text-[10px] text-zinc-500 truncate">
                                                {((char.costumes || []).length + (char.costumeAndAccessoryImages || []).length)} costume(s)
                                            </p>
                                        </div>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <div className="p-6 pt-3 border-t border-zinc-800 flex gap-2.5">
                    <button
                        type="button"
                        data-testid="try-on-skip-btn"
                        onClick={() => onResolve([])}
                        className="flex-1 px-4 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 text-sm font-medium transition-colors"
                    >
                        Skip Try-On
                    </button>
                    <button
                        type="button"
                        data-testid="try-on-run-btn"
                        disabled={noneSelected}
                        onClick={() => onResolve(Array.from(selectedIds))}
                        className="flex-1 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors"
                    >
                        Run Try-On{noneSelected ? '' : ` (${selectedIds.size})`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export const CharacterSelection = ({ 
    initialData, 
    onSubmit, 
    onBack, 
    error, 
    onGenerateFullVideo,
    onDeductCredits,
    onSaveMedia,
    onProjectUpdate,
    onTrackCreditsUsed,
    onRefreshCredits,
    onAutoSave
}) => {
    const [characters, setCharacters] = useState(initialData.characters || []);
    const charactersRef = useRef(characters);
    charactersRef.current = characters;
    const [editingCharacter, setEditingCharacter] = useState(null);
    const [activeTab, setActiveTab] = useState('characters');
    // Phase 3.1 (Feb 2026): Accordion expand state for character / element
    // cards. Single-expand — opening one row collapses the previously open
    // one. Defaults to the first row of the active tab so the screen never
    // starts "empty" looking.
    const [expandedCharId, setExpandedCharId] = useState(null);
    // Phase 3.1 (Feb 2026): Narrator tab is only meaningful when the script
    // generation actually includes narration. Derived from the project's
    // audioStyle ('dialogue' | 'narration' | 'both' | 'asmr' | 'none').
    //
    // Nov 2026 patch — Also show the tab whenever the backend actually
    // returned narrator(s) in the cast, regardless of audioStyle. This fixes
    // cases where the LLM identified a narrator in the script but the
    // project's `audioStyle` field is unset, `'dialogue'`, or otherwise
    // inconsistent with the parsed cast. The audioStyle gate is retained
    // so the tab still appears on empty-cast projects that were configured
    // for narration (users can add a narrator manually).
    const narratorHasEntries = (characters || []).some(c => c.category === 'narrator');
    const narratorTabVisible = narratorHasEntries || ['narration', 'both'].includes(initialData.audioStyle);
    const [cloningStatus, setCloningStatus] = useState({});
    const [viewingImage, setViewingImage] = useState(null); // { url: string, name: string }
    const [assetsModalCharId, setAssetsModalCharId] = useState(null); // char.id when modal is open
    const [libraryModalCharId, setLibraryModalCharId] = useState(null); // char.id for actors library
    const [costumeAssetsCharId, setCostumeAssetsCharId] = useState(null); // char.id for costume user assets
    const [costumeLibraryCharId, setCostumeLibraryCharId] = useState(null); // char.id for costume assets library
    const [generatingCostumeFor, setGeneratingCostumeFor] = useState(null); // char.id while generating
    const [generatingPhotoFor, setGeneratingPhotoFor] = useState(null);
    // One-shot reference images per character (not persisted): { [charId]: [url] }
    const [photoRefs, setPhotoRefs] = useState({});
    const [costumeRefs, setCostumeRefs] = useState({});
    const [editingCostume, setEditingCostume] = useState(null); // { charId, index, url, name }
    const [autoGenState, setAutoGenState] = useState(null); // { current, total, name, step } or null
    const [tryOnConfirm, setTryOnConfirm] = useState(null); // { count, resolve } when awaiting user choice
    // Feb 2026 — Inline voice controls: which character is picking a voice
    const [voiceLibraryCharId, setVoiceLibraryCharId] = useState(null);
    const [voiceClonerCharId,  setVoiceClonerCharId]  = useState(null);
    const audioFileRefs = useRef({});
    const imageFileRefs = useRef({});
    const costumeFileRefs = useRef({});

    // Helper: persist a generated image (fal.ai URL) to GCS
    // Returns GCS URL, or falls back to base64 data URI.
    // Apr 2026: when the URL is already on storage.googleapis.com (server-side persist), short-circuit.
    const persistToGCS = async (imageUrl) => {
        if (!imageUrl) return imageUrl;
        if (isAlreadyPersisted(imageUrl)) return imageUrl;
        if (imageUrl.startsWith('data:')) {
            // Already base64 — upload to GCS directly
            try {
                const userEmail = sessionStorage.getItem('userEmail');
                const gcsUrl = await uploadDataUriToBackend(imageUrl, userEmail);
                if (gcsUrl) { console.log('✅ Uploaded base64 to GCS:', gcsUrl.substring(0, 80)); return gcsUrl; }
            } catch (e) { console.warn('⚠️ GCS upload of base64 failed, keeping data URI:', e.message); }
            return imageUrl;
        }
        if (!imageUrl.startsWith('http')) return imageUrl;
        try {
            console.log('🔄 Persisting to GCS:', imageUrl.substring(0, 80));
            const base64 = await fetchImageViaProxy(imageUrl);
            if (!base64 || !base64.startsWith('data:')) { console.warn('⚠️ Proxy conversion failed, keeping original URL'); return imageUrl; }
            const userEmail = sessionStorage.getItem('userEmail');
            const gcsUrl = await uploadDataUriToBackend(base64, userEmail);
            if (gcsUrl) { console.log('✅ Persisted to GCS:', gcsUrl.substring(0, 80)); return gcsUrl; }
            return base64; // fallback to base64 if GCS fails
        } catch (e) {
            console.warn('⚠️ Failed to persist to GCS:', e.message);
            return imageUrl;
        }
    };

    // Build a professional costume designer prompt from script + character context
    // NOTE: `buildCostumePrompt` lives in `src/utils/costumePrompt.js` so the
    // manual button here and the pre-storyboard pipeline produce identical
    // prompts. Do NOT re-introduce a local copy — keep one source of truth.

    // Sync local characters state with initialData when it changes from parent
    useEffect(() => {
        if (initialData.characters) {
            console.log('🔄 Syncing characters from initialData:', initialData.characters.length);
            setCharacters(initialData.characters);
        }
    }, [initialData.characters]);

    const updateCharacters = (newCharacters) => {
        console.log('📝 updateCharacters called with', newCharacters.length, 'characters');
        setCharacters(newCharacters);
        if (onProjectUpdate) {
            console.log('📤 Calling onProjectUpdate');
            onProjectUpdate({ ...initialData, characters: newCharacters });
        }
    };

    const handleCharacterUpdate = (id, field, value) => {
        const newCharacters = characters.map(char => char.id === id ? { ...char, [field]: value } : char);
        updateCharacters(newCharacters);
    };

    const handleAddCharacter = () => {
        const category = activeTab === 'characters' ? 'character' : activeTab === 'elements' ? 'element' : 'narrator';
        const newChar = {
            id: `char-${Date.now()}`,
            name: '',
            description: '',
            category,
            makeupLevel: 'natural',
        };
        updateCharacters([...characters, newChar]);
    };

    const handleRemoveCharacter = (id) => {
        // Feb 2026 — Free the ElevenLabs slot if this character owns a
        // CLONED voice (library voices are shared and must not be deleted).
        const victim = characters.find(c => c.id === id);
        const clonedVoiceId = victim?.voiceSource === 'cloned' ? victim?.voiceId : null;
        if (clonedVoiceId) {
            // Fire-and-forget — UI doesn't wait on the network round-trip.
            deleteClonedVoice(clonedVoiceId);
        }
        updateCharacters(characters.filter(char => char.id !== id));
    };

    const handleCloneCharacter = (char) => {
        const cloned = {
            ...char,
            id: `char-${Date.now()}`,
            name: `${char.name} (Copy)`,
            image: undefined,
            audio: undefined,
            // Don't carry the ElevenLabs voice into the clone — the clone
            // should pick its own voice.
            elevenLabsVoiceId: undefined,
            voiceId: undefined,
            voiceName: undefined,
            voiceSource: undefined,
        };
        updateCharacters([...characters, cloned]);
    };

    // Feb 2026 — Inline voice handlers used by the card-level Voice section.
    const handleVoicePickedInline = (charId, voiceId, voiceName) => {
        const patch = {
            voiceId,
            elevenLabsVoiceId: voiceId,
            voiceName: voiceName || 'ElevenLabs voice',
            voiceSource: 'library',
        };
        updateCharacters(characters.map(c => c.id === charId ? { ...c, ...patch } : c));
        setVoiceLibraryCharId(null);
    };
    const handleVoiceClonedInline = (charId, { voiceId, voiceName }) => {
        const patch = {
            voiceId,
            elevenLabsVoiceId: voiceId,
            voiceName: voiceName || 'Cloned voice',
            voiceSource: 'cloned',
        };
        updateCharacters(characters.map(c => c.id === charId ? { ...c, ...patch } : c));
    };
    const handleRemoveVoiceInline = async (char) => {
        if (char?.voiceSource === 'cloned' && char?.voiceId) {
            const ok = window.confirm(`Delete cloned voice "${char.voiceName || char.voiceId}"? This frees the ElevenLabs slot and cannot be undone.`);
            if (!ok) return;
            await deleteClonedVoice(char.voiceId);
        }
        updateCharacters(characters.map(c => c.id === char.id ? {
            ...c,
            voiceId: undefined,
            elevenLabsVoiceId: undefined,
            voiceName: undefined,
            voiceSource: undefined,
        } : c));
    };


    const handleEditPortrait = (char) => {
        setEditingCharacter(char);
    };

    const handleSavePortrait = (characterId, updates) => {
        console.log('💾 handleSavePortrait called:', {
            characterId,
            name: updates.name,
            category: updates.category,
            hasImage: !!updates.image,
            currentCharactersCount: characters.length,
            characterIds: characters.map(c => c.id)
        });
        
        // Check if this is a new character that wasn't in the list
        const existingChar = characters.find(c => c.id === characterId);
        console.log('  Existing character found:', !!existingChar);
        
        let updatedCharacters;
        if (existingChar) {
            // Update existing character
            updatedCharacters = characters.map(char => 
                char.id === characterId ? { 
                    ...char, 
                    name: updates.name,
                    description: updates.description,
                    image: updates.image,
                    category: updates.category || char.category,
                    referenceImages: updates.likenessImages,
                    costumeAndAccessoryImages: updates.costumeImages,
                    makeupLevel: updates.makeupLevel
                } : char
            );
        } else {
            // New character - add it
            const newChar = {
                id: characterId,
                name: updates.name,
                description: updates.description,
                image: updates.image,
                category: updates.category || 'character',
                referenceImages: updates.likenessImages,
                costumeAndAccessoryImages: updates.costumeImages,
                makeupLevel: updates.makeupLevel
            };
            updatedCharacters = [...characters, newChar];
            console.log('  Added new character to list');
        }
        
        console.log('  Final characters count:', updatedCharacters.length);
        
        setCharacters(updatedCharacters);
        if (onProjectUpdate) {
            console.log('  Calling onProjectUpdate with updated characters');
            onProjectUpdate({ ...initialData, characters: updatedCharacters });
        } else {
            console.warn('  ⚠️ onProjectUpdate is not defined!');
        }
        setEditingCharacter(null);
    };

    const handleAudioFileChange = async (e, id) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Credits are deducted by backend - removed upfront deduction

        setCloningStatus(prev => ({ ...prev, [id]: { status: 'cloning' } }));
        
        try {
            const base64String = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            
            const character = characters.find(c => c.id === id);
            if (!character) throw new Error("Character not found");

            const result = await cloneVoiceWithElevenLabs(character.name, base64String);
            
            // Handle both old format (string) and new format (object with credits)
            const voiceId = result?.voiceId || result;

            const newCharacters = characters.map(char => 
                char.id === id 
                ? { 
                    ...char, 
                    audio: { name: file.name, data: base64String }, 
                    elevenLabsVoiceId: voiceId 
                  } 
                : char
            );
            updateCharacters(newCharacters);
            setCloningStatus(prev => ({ ...prev, [id]: { status: 'cloned' } }));
            
            // Track credits from backend response
            if (onTrackCreditsUsed && result?.creditsUsed) {
                onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
            }

        } catch (err) {
            const errorMessage = err.message || "An unknown error occurred during cloning.";
            console.error("Voice cloning failed:", err);
            setCloningStatus(prev => ({ ...prev, [id]: { status: 'error', error: errorMessage } }));
            alert(errorMessage);
        }
    };

    const handleImageFileChange = async (e, id) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = async () => {
            let base64String = reader.result;
            
            try {
                base64String = await cropImageToAspectRatio(base64String, initialData.aspectRatio);
            } catch (e) {
                console.error("Failed to auto-crop image:", e);
            }

            handleCharacterUpdate(id, 'image', base64String);
            if (onSaveMedia) onSaveMedia([base64String]);
        };
        reader.readAsDataURL(file);
    };

    const handleAssetSelect = (imageUrl, charId, asset = null) => {
        // Use the URL directly — external URLs can't be cropped via canvas (CORS)
        handleCharacterUpdate(charId, 'image', imageUrl);
        if (onSaveMedia) onSaveMedia([imageUrl]);

        // Feb 2026 — When the selected asset is an ACTOR that ALREADY has a
        // voice_id stored in user backend, carry that voice onto the
        // character record so the user doesn't have to re-pick/clone it.
        // Also carry name / description / actor_id / image_gallery when the
        // character field is empty (don't clobber user-typed values).
        if (asset) {
            const voiceId   = asset.voice_id || asset.voiceId || null;
            const actorId   = asset.actor_id || asset.actorId || null;
            const actorName = asset.name || '';
            const actorDesc = asset.description || asset.desc || '';
            const gallery   = asset.image_gallery || asset.imageGallery || null;
            // Feb 2026 — carry over BytePlus ModelArk (Seedance) asset refs
            // from the user-assets catalog so Seedance video generation can
            // reuse the pre-approved actor asset.
            const byteplusAssetGroupId =
                asset.byteplus_asset_group_id ||
                asset.byteplusAssetGroupId ||
                asset.seedance_asset_group_id ||
                null;
            const byteplusAssetId =
                asset.byteplus_asset_id ||
                asset.byteplusAssetId ||
                asset.seedance_asset_id ||
                null;
            const useByteplusForSeedance =
                asset.use_byteplus_for_seedance ??
                asset.useByteplusForSeedance ??
                Boolean(byteplusAssetGroupId && byteplusAssetId);

            const newCharacters = characters.map(c => {
                if (c.id !== charId) return c;
                const updated = { ...c, image: imageUrl };
                if (voiceId) {
                    updated.voiceId = voiceId;
                    updated.elevenLabsVoiceId = voiceId;
                    updated.voiceName = actorName || asset.voice_name || 'Actor voice';
                    // Actor-attached voices are library-managed (uploaded by the
                    // user in their asset library, not cloned in this session).
                    // We do NOT own the delete lifecycle here, so treat as library.
                    updated.voiceSource = asset.voice_source === 'cloned' ? 'cloned' : 'library';
                }
                updated.actorId = actorId; // null for non-actor assets so backfill can't re-attach an old actor's asset
                if (actorName && !c.name?.trim())        updated.name         = actorName;
                if (actorDesc && !c.description?.trim())  updated.description = actorDesc;
                if (Array.isArray(gallery) && gallery.length && !(c.imageGallery?.length)) {
                    updated.imageGallery = gallery;
                }
                // Always overwrite so a previous actor's asset never sticks to a new photo.
                updated.byteplusAssetGroupId = byteplusAssetGroupId;
                updated.byteplusAssetId      = byteplusAssetId;
                updated.byteplusAssetStatus  = asset.byteplus_asset_status || asset.byteplusAssetStatus || null;
                updated.useByteplusForSeedance = Boolean(byteplusAssetGroupId && byteplusAssetId && useByteplusForSeedance);
                return updated;
            });
            updateCharacters(newCharacters);
        }

        setAssetsModalCharId(null);
    };

    const handleActorLibrarySelect = (actorData, charId) => {
        const newCharacters = characters.map(char => {
            if (char.id !== charId) return char;
            const updated = { ...char, image: actorData.imageUrl };
            if (actorData.name && !char.name?.trim()) updated.name = actorData.name;
            if (actorData.description && !char.description?.trim()) updated.description = actorData.description;
            // Feb 2026 — library actors that ship with a preset voice_id
            // now surface as a proper Voice pill on the card.
            if (actorData.voiceId) {
                updated.voiceId = actorData.voiceId;
                updated.elevenLabsVoiceId = actorData.voiceId;
                updated.voiceName   = actorData.voiceName || actorData.name || 'Library voice';
                updated.voiceSource = actorData.voiceSource === 'cloned' ? 'cloned' : 'library';
            }
            if (actorData.actorId) updated.actorId = actorData.actorId;
            if (actorData.imageGallery?.length) updated.imageGallery = actorData.imageGallery;
            // Feb 2026 — persist BytePlus ModelArk (Seedance) asset refs so
            // downstream Seedance video generation can reuse the pre-approved
            // actor asset instead of re-uploading reference imagery.
            // Always overwrite so a previous actor's asset never sticks to a new photo.
            updated.byteplusAssetGroupId = actorData.byteplusAssetGroupId || null;
            updated.byteplusAssetId      = actorData.byteplusAssetId || null;
            updated.byteplusAssetStatus  = actorData.byteplusAssetStatus || null;
            updated.useByteplusForSeedance = Boolean(actorData.byteplusAssetGroupId && actorData.byteplusAssetId && actorData.useByteplusForSeedance);
            return updated;
        });
        updateCharacters(newCharacters);
        if (actorData.imageUrl && onSaveMedia) onSaveMedia([actorData.imageUrl]);
        setLibraryModalCharId(null);
    };

    const handleCostumeUpload = (e, charId) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = async () => {
            const url = await persistToGCS(reader.result);
            updateCharacters(charactersRef.current.map(c =>
                c.id === charId ? { ...c, costumes: [...(c.costumes || []), url] } : c
            ));
            if (onSaveMedia) onSaveMedia([url]);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleCostumeAssetSelect = async (rawUrl, charId) => {
        setCostumeAssetsCharId(null);
        const imageUrl = rawUrl?.startsWith('data:') ? await persistToGCS(rawUrl) : rawUrl;
        updateCharacters(charactersRef.current.map(c =>
            c.id === charId ? { ...c, costumes: [...(c.costumes || []), imageUrl] } : c
        ));
        if (onSaveMedia) onSaveMedia([imageUrl]);
    };

    const handleSelectCostumeForVideo = (charId, url) => {
        updateCharacters(characters.map(c => c.id === charId ? { ...c, selectedCostumeUrl: url } : c));
    };

    const handleCostumeLibrarySelect = (actorData, charId) => {
        const imageUrl = actorData.imageUrl;
        if (!imageUrl) return;
        const newCharacters = characters.map(c =>
            c.id === charId ? { ...c, costumes: [...(c.costumes || []), imageUrl] } : c
        );
        updateCharacters(newCharacters);
        if (onSaveMedia) onSaveMedia([imageUrl]);
        setCostumeLibraryCharId(null);
    };

    const handleRemoveCostume = (charId, index) => {
        const newCharacters = characters.map(c => {
            if (c.id !== charId) return c;
            const costumes = [...(c.costumes || [])];
            const [removed] = costumes.splice(index, 1);
            return { ...c, costumes, selectedCostumeUrl: c.selectedCostumeUrl === removed ? null : c.selectedCostumeUrl };
        });
        updateCharacters(newCharacters);
    };

    const handleGenerateCostume = async (char) => {
        setGeneratingCostumeFor(char.id);
        try {
            const script = initialData?.script || '';
            const prompt = buildCostumePrompt(char.name, char.description, script);

            const refImages = costumeRefs[char.id] || [];
            const fullPrompt = refImages.length > 0
                ? `${prompt}\nUse the attached reference images as visual guidance for the garments, fabrics, colours and accessories.`
                : prompt;
            const result = await generateImage(fullPrompt, 1, 'default', '1:1', refImages, true, null);

            const imageUrl = result?.images?.[0] || result?.imageUrl;
            if (imageUrl) {
                const gcsUrl = await persistToGCS(imageUrl);
                updateCharacters(charactersRef.current.map(c =>
                    c.id === char.id ? { ...c, costumes: [...(c.costumes || []), gcsUrl] } : c
                ));
                setCostumeRefs(prev => ({ ...prev, [char.id]: [] }));
                if (onSaveMedia) onSaveMedia([gcsUrl]);
                if (result.creditsUsed && onTrackCreditsUsed) onTrackCreditsUsed(result.creditsUsed);
            }
        } catch (err) {
            console.error('Failed to generate costume:', err);
        } finally {
            setGeneratingCostumeFor(null);
        }
    };

    const handleGeneratePhoto = async (char) => {
        const refs = photoRefs[char.id] || [];
        const aspectRatio = initialData?.aspectRatio || '16:9';
        setGeneratingPhotoFor(char.id);
        try {
            let imageUrl, creditsUsed;
            if (char.category === 'element') {
                const result = await generateElementImage(char.description || char.name, refs, aspectRatio, 'default');
                imageUrl = result?.imageUrl || result?.images?.[0];
                creditsUsed = result?.creditsUsed;
            } else {
                const sheetPrompt = buildCharacterSheetPrompt(char.name, char.description, aspectRatio)
                    + (refs.length > 0 ? '\nMatch the face, hair and overall look of the person in the attached reference images.' : '');
                const result = await generateCharacterPortrait(sheetPrompt, refs, [], 'natural', aspectRatio, 'character', 'default');
                imageUrl = result?.image || result?.imageUrl || result?.images?.[0];
                creditsUsed = result?.creditsUsed || CREDIT_COSTS.CHARACTER_PORTRAIT;
            }
            if (!imageUrl) throw new Error('No image returned');
            const gcsUrl = await persistToGCS(imageUrl);
            // A newly generated face no longer matches any library actor's BytePlus asset.
            updateCharacters(charactersRef.current.map(c => c.id === char.id
                ? { ...c, image: gcsUrl, byteplusAssetGroupId: null, byteplusAssetId: null, useByteplusForSeedance: false }
                : c));
            setPhotoRefs(prev => ({ ...prev, [char.id]: [] }));
            if (onSaveMedia) onSaveMedia([gcsUrl]);
            if (onTrackCreditsUsed && creditsUsed) onTrackCreditsUsed(creditsUsed);
        } catch (err) {
            console.error('Failed to generate photo:', err);
            alert(`Photo generation failed: ${err.message}`);
        } finally {
            setGeneratingPhotoFor(null);
        }
    };

    const handleApplyCostumeEdit = async (editedUrl) => {
        const { charId, url: oldUrl, kind } = editingCostume;
        const gcsUrl = await persistToGCS(editedUrl);
        if (kind === 'element') {
            updateCharacters(charactersRef.current.map(c => (c.id === charId ? { ...c, image: gcsUrl } : c)));
            if (onSaveMedia) onSaveMedia([gcsUrl]);
            setEditingCostume(null);
            return;
        }
        updateCharacters(charactersRef.current.map(c => {
            if (c.id !== charId) return c;
            const swap = (arr) => Array.isArray(arr) ? arr.map(u => (u === oldUrl ? gcsUrl : u)) : arr;
            return {
                ...c,
                costumes: swap(c.costumes),
                costumeAndAccessoryImages: swap(c.costumeAndAccessoryImages),
                selectedCostumeUrl: c.selectedCostumeUrl === oldUrl ? gcsUrl : c.selectedCostumeUrl,
            };
        }));
        if (onSaveMedia) onSaveMedia([gcsUrl]);
        setEditingCostume(null);
    };

    const [tryOnGenerating, setTryOnGenerating] = useState(null); // char.id while generating

    const handleTryOn = async (char) => {
        const costumeImgs = [...(char.costumeAndAccessoryImages || []), ...(char.costumes || [])].filter(Boolean);
        if (!char.image || costumeImgs.length === 0) {
            alert('Character needs both a portrait and at least one costume image for try-on.');
            return;
        }
        setTryOnGenerating(char.id);
        try {
            const aspectRatio = initialData?.aspectRatio || '16:9';
            // Regenerate the FULL 8-panel character sheet with the try-on costume
            // applied across every panel. Same layout as the existing sheet —
            // only the outfit changes (face/hair/body locked via the character
            // reference image, costume locked via the costume reference images).
            const tryOnPrompt = buildCharacterSheetPrompt(char.name, char.description, aspectRatio, { tryOnCostume: true });
            const result = await generateCharacterPortrait(
                tryOnPrompt,
                [char.image],
                costumeImgs,
                '',
                aspectRatio,
                'character',
                'default'
            );
            const tryOnUrl = result?.image || result?.imageUrl || result?.images?.[0];
            if (tryOnUrl) {
                const gcsUrl = await persistToGCS(tryOnUrl);
                const updated = characters.map(c => c.id === char.id ? {
                    ...c,
                    originalPortrait: c.originalPortrait || c.image,
                    image: gcsUrl
                } : c);
                updateCharacters(updated);
                if (onSaveMedia) onSaveMedia([gcsUrl]);
                if (result?.creditsUsed && onTrackCreditsUsed) onTrackCreditsUsed(result.creditsUsed);
            }
        } catch (err) {
            console.error(`Try-on failed for ${char.name}:`, err);
            alert(`Try-on failed: ${err.message}`);
        } finally {
            setTryOnGenerating(null);
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        // Auto-generation (portraits / costumes / try-on) has been moved to
        // the Tier Selection step so users can pick their model tier before
        // any credits are spent. Here we only validate and hand off.
        const validChars = characters.filter(c => c.name.trim() && c.description.trim());
        updateCharacters(validChars);
        onSubmit(validChars);
    };

    const filteredCharacters = characters.filter(char => {
        if (activeTab === 'characters') return char.category === 'character' || !char.category;
        if (activeTab === 'elements') return char.category === 'element';
        if (activeTab === 'narrator') return char.category === 'narrator';
        return false;
    });

    const characterCount = characters.filter(c => c.category === 'character' || !c.category).length;
    const elementCount = characters.filter(c => c.category === 'element').length;
    const narratorCount = characters.filter(c => c.category === 'narrator').length;

    // Phase 3.1: If the narrator tab is hidden (no narration in script) but
    // the user happens to land on it (e.g. from a saved project that used to
    // have narration), bounce them back to Characters so they aren't stuck.
    useEffect(() => {
        if (!narratorTabVisible && activeTab === 'narrator') setActiveTab('characters');
    }, [narratorTabVisible, activeTab]);

    // Phase 3.1: When the active tab or filtered list changes, default the
    // expanded row to the first item so the screen always shows real content.
    useEffect(() => {
        if (filteredCharacters.length === 0) {
            setExpandedCharId(null);
            return;
        }
        if (!filteredCharacters.find(c => c.id === expandedCharId)) {
            setExpandedCharId(filteredCharacters[0].id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, filteredCharacters.length]);

    return (
        <div className="w-full h-full overflow-y-auto p-8 bg-[#0a0a0f] relative">
            {/* Auto-generation progress overlay */}
            {autoGenState && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" data-testid="auto-gen-overlay">
                    <div className="bg-[#111119] border border-gray-800 rounded-2xl p-8 max-w-md w-full text-center space-y-4">
                        <div className="w-10 h-10 border-3 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
                        <h3 className="text-lg font-bold text-white">Preparing Your Cast</h3>
                        <p className="text-sm text-gray-400">
                            Generating {autoGenState.step === 'portrait' ? 'portrait' : autoGenState.step === 'costume' ? 'costume' : 'costume try-on'} for <span className="text-purple-400 font-semibold">{autoGenState.name}</span>
                        </p>
                        <div className="w-full bg-gray-800 rounded-full h-2">
                            <div className="bg-purple-500 h-2 rounded-full transition-all duration-500" style={{ width: `${(autoGenState.current / autoGenState.total) * 100}%` }} />
                        </div>
                        <p className="text-xs text-gray-600">{autoGenState.current} of {autoGenState.total} items</p>
                    </div>
                </div>
            )}

            <div className="w-full max-w-5xl mx-auto">
                <div className="flex items-start gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-violet-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                    </div>
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Meet your cast</h2>
                        <p className="text-sm text-zinc-400 mt-1">We've pulled the people, things, and narrators out of your script. Add a photo, tweak a description, then we'll bring them to life on screen.</p>
                    </div>
                </div>

                {error && (
                    <div className="mt-4 mb-4 p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-sm text-rose-300">
                        {error}
                    </div>
                )}

                {/* Step 1: pick what you want to edit */}
                <div className="flex items-center gap-2 mt-6 mb-3">
                    <div className="w-6 h-6 rounded-full bg-violet-500/20 border border-violet-500/40 flex items-center justify-center text-[11px] font-bold text-violet-300">1</div>
                    <div className="text-sm font-semibold text-zinc-200">Pick what you'd like to review</div>
                </div>

                {/* Tabs — Phase 3 (Feb 2026): numbered pill bar acting as a
                    sub-stepper inside the Cast & World screen. Active pill
                    is highlighted; the others remain freely navigable so
                    the user can jump between Characters / Elements / Narrator
                    in any order. */}
                <div className="flex items-stretch gap-2 mb-6" data-testid="cast-world-tabs">
                    {[
                        { id: 'characters', n: 1, label: 'Characters',       count: characterCount, desc: 'People who appear on screen' },
                        { id: 'elements',   n: 2, label: 'Props & Elements', count: elementCount,   desc: 'Objects, vehicles, products' },
                        { id: 'narrator',   n: 3, label: 'Narrator',         count: narratorCount,  desc: 'Voice-over only' },
                    ].filter(t => t.id !== 'narrator' || narratorTabVisible).map(t => {
                        const isActive = activeTab === t.id;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setActiveTab(t.id)}
                                className={`flex-1 flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all text-left ${
                                    isActive
                                        ? 'bg-violet-500/15 border-violet-500/50 ring-2 ring-violet-500/30'
                                        : 'bg-white/[0.03] border-white/10 hover:border-white/25 hover:bg-white/[0.06]'
                                }`}
                                data-testid={`cast-world-tab-${t.id}`}
                            >
                                <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                    isActive ? 'bg-violet-500 text-white' : 'bg-white/10 text-zinc-400'
                                }`}>{t.n}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-1.5">
                                        <span className={`text-sm font-semibold truncate ${isActive ? 'text-white' : 'text-zinc-200'}`}>{t.label}</span>
                                        <span className={`text-[11px] tabular-nums ${isActive ? 'text-violet-200' : 'text-zinc-500'}`}>({t.count})</span>
                                    </div>
                                    <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{t.desc}</div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Step 2: edit the entries */}
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-full bg-violet-500/20 border border-violet-500/40 flex items-center justify-center text-[11px] font-bold text-violet-300">2</div>
                    <div className="text-sm font-semibold text-zinc-200">
                        {activeTab === 'characters' && `Review your ${characterCount} character${characterCount === 1 ? '' : 's'}`}
                        {activeTab === 'elements'   && `Review your ${elementCount} prop${elementCount === 1 ? '' : 's'} & element${elementCount === 1 ? '' : 's'}`}
                        {activeTab === 'narrator'   && `Set up your narrator${narratorCount > 1 ? 's' : ''}`}
                    </div>
                </div>
                <div className="mb-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-sm text-amber-200/90 flex gap-2 items-start">
                    <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                    <div><span className="font-semibold">Tip:</span> Don't worry about the photos — leave them blank and we'll generate one in the next step. You can always tweak descriptions to influence the look.</div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Character Cards — Phase 3.1 (Feb 2026):
                        Accordion layout. Each row is collapsed by default
                        showing only a small thumbnail + name + description.
                        Clicking the header expands the row to reveal the
                        large Photo + Costume slots and all action buttons
                        flat (no dropdown). Single-expand: opening one row
                        collapses the previously open one. */}
                    <div className="space-y-3">
                        {filteredCharacters.map((char) => {
                            if (!audioFileRefs.current[char.id]) audioFileRefs.current[char.id] = React.createRef();
                            if (!imageFileRefs.current[char.id]) imageFileRefs.current[char.id] = React.createRef();
                            const isExpanded = expandedCharId === char.id;
                            const isElement  = char.category === 'element';
                            const isNarrator = char.category === 'narrator';
                            const showCostume = !isElement && !isNarrator;
                            const thumbUrl = char.image || null;
                            const previewDescription = (char.description || '').trim() || 'Tap to set photo and details.';

                            return (
                                <div
                                    key={char.id}
                                    className={`rounded-2xl border transition-colors ${
                                        isExpanded
                                            ? 'bg-violet-500/[0.04] border-violet-500/40'
                                            : 'bg-white/[0.02] border-white/10 hover:border-white/25'
                                    }`}
                                    data-testid={`character-card-${char.id}`}
                                >
                                    {/* Collapsed header (always visible) */}
                                    <button
                                        type="button"
                                        onClick={() => setExpandedCharId(isExpanded ? null : char.id)}
                                        className="w-full flex items-center gap-4 px-4 py-3 text-left"
                                        data-testid={`character-card-header-${char.id}`}
                                        aria-expanded={isExpanded}
                                    >
                                        {/* Thumb */}
                                        <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-violet-500/10 border border-violet-500/30 flex items-center justify-center overflow-hidden">
                                            {thumbUrl ? (
                                                <img src={thumbUrl} alt={char.name || 'Character'} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                            ) : (
                                                <span className="text-[10px] font-semibold uppercase tracking-widest text-violet-300">Add</span>
                                            )}
                                        </div>
                                        {/* Name + desc */}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-base font-semibold text-white truncate">
                                                {char.name || (isElement ? 'Untitled element' : isNarrator ? 'Untitled narrator' : 'Untitled character')}
                                            </div>
                                            <div className="text-xs text-zinc-400 mt-0.5 truncate">{previewDescription}</div>
                                            {/* Feb 2026 — BytePlus ModelArk (Seedance) asset badge.
                                                Visible only when the admin backend attached a
                                                pre-approved asset to this actor. Lets the user
                                                confirm at a glance that the IDs were captured. */}
                                            {(char.byteplusAssetGroupId && char.byteplusAssetId) && (
                                                <div
                                                    className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-[10px] font-mono text-amber-200"
                                                    title={`Seedance asset — Group: ${char.byteplusAssetGroupId}\nAsset: ${char.byteplusAssetId}${char.useByteplusForSeedance === false ? '\n(opted out)' : ''}`}
                                                    data-testid={`seedance-asset-badge-${char.id}`}
                                                >
                                                    <span className="uppercase tracking-wider text-amber-300/80">Seedance</span>
                                                    <span className="truncate max-w-[160px]">{char.byteplusAssetId}</span>
                                                </div>
                                            )}
                                        </div>
                                        {/* Chevron */}
                                        <svg className={`flex-shrink-0 w-4 h-4 text-zinc-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {/* Expanded body */}
                                    {isExpanded && (
                                        <div className="px-4 pb-4 border-t border-white/5 pt-4 space-y-4" data-testid={`character-card-body-${char.id}`}>
                                            {/* Name + Description editable */}
                                            <div className="space-y-2">
                                                <input
                                                    type="text"
                                                    value={char.name}
                                                    onChange={(e) => handleCharacterUpdate(char.id, 'name', e.target.value)}
                                                    placeholder={isElement ? 'Element name' : isNarrator ? 'Narrator name' : 'Character name'}
                                                    className="w-full px-3 py-2 bg-zinc-900 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm font-semibold text-white"
                                                    data-testid={`character-name-${char.id}`}
                                                />
                                                <textarea
                                                    value={char.description}
                                                    onChange={(e) => handleCharacterUpdate(char.id, 'description', e.target.value)}
                                                    placeholder="Describe appearance, personality, or details…"
                                                    rows={2}
                                                    className="w-full px-3 py-2 bg-zinc-900 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none text-sm text-white"
                                                    data-testid={`character-description-${char.id}`}
                                                />
                                            </div>

                                            {/* Photo + Costume slots */}
                                            {!isNarrator && (
                                                <div className={`grid gap-4 ${showCostume ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                                                    {/* PHOTO */}
                                                    <div>
                                                        <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-widest mb-2">Photo</p>
                                                        <div className="aspect-square rounded-2xl border-2 border-dashed border-violet-500/30 bg-violet-500/[0.03] flex items-center justify-center overflow-hidden relative group">
                                                            {char.image ? (
                                                                <>
                                                                    <img src={char.image} alt={char.name || 'Character'} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                                        <button type="button" onClick={() => setViewingImage({ url: char.image, name: char.name })}
                                                                            className="p-2 bg-blue-600 hover:bg-blue-500 rounded-full text-white" title="View image"
                                                                            data-testid={`photo-view-${char.id}`}>
                                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                                                        </button>
                                                                        {isElement && (
                                                                            <button type="button" onClick={() => setEditingCostume({ charId: char.id, url: char.image, name: char.name, kind: 'element' })}
                                                                                className="p-2 bg-violet-600 hover:bg-violet-500 rounded-full text-white" title="Edit image"
                                                                                data-testid={`element-edit-${char.id}`}>
                                                                                <Pencil className="w-4 h-4" />
                                                                            </button>
                                                                        )}
                                                                        <button type="button" onClick={() => handleCharacterUpdate(char.id, 'image', null)}
                                                                            className="p-2 bg-red-600 hover:bg-red-500 rounded-full text-white" title="Delete image"
                                                                            data-testid={`photo-delete-${char.id}`}>
                                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="text-center px-4">
                                                                    <div className="w-12 h-16 rounded-lg bg-violet-500/15 border border-violet-500/30 mx-auto mb-3" />
                                                                    <div className="text-sm font-semibold text-violet-200">No photo yet</div>
                                                                    <div className="text-[11px] text-zinc-500 mt-1">Generate one or upload your own — it&apos;ll preview here</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {/* Action buttons — Phase 3.1 flat layout */}
                                                        <input type="file" ref={imageFileRefs.current[char.id]} accept="image/*" onChange={(e) => handleImageFileChange(e, char.id)} className="hidden" />
                                                        <div className="flex flex-wrap gap-2 mt-3">
                                                            <button type="button" onClick={() => handleGeneratePhoto(char)}
                                                                disabled={generatingPhotoFor === char.id}
                                                                className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5"
                                                                data-testid={`photo-generate-${char.id}`}>
                                                                {generatingPhotoFor === char.id ? (
                                                                    <><div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" /> Generating…</>
                                                                ) : (
                                                                    <><Wand2 className="w-3.5 h-3.5" /> Generate</>
                                                                )}
                                                            </button>
                                                            <button type="button" onClick={() => imageFileRefs.current[char.id].current?.click()}
                                                                className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                                                                data-testid={`photo-upload-${char.id}`}>
                                                                <Upload className="w-3.5 h-3.5" /> Upload
                                                            </button>
                                                            <button type="button" onClick={() => setAssetsModalCharId(char.id)}
                                                                className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                                                                data-testid={`photo-user-assets-${char.id}`}>
                                                                <FolderOpen className="w-3.5 h-3.5" /> User assets
                                                            </button>
                                                            <button type="button" onClick={() => setLibraryModalCharId(char.id)}
                                                                className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                                                                data-testid={`photo-library-${char.id}`}>
                                                                <Users className="w-3.5 h-3.5" /> Assets library
                                                            </button>
                                                            {isElement && char.image && (
                                                                <button type="button" onClick={() => setEditingCostume({ charId: char.id, url: char.image, name: char.name, kind: 'element' })}
                                                                    className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                                                                    data-testid={`element-edit-btn-${char.id}`}>
                                                                    <Pencil className="w-3.5 h-3.5" /> Edit
                                                                </button>
                                                            )}
                                                            {!isElement && (
                                                                <button type="button" onClick={() => handleEditPortrait(char)}
                                                                    className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                                                                    data-testid={`photo-editor-${char.id}`}>
                                                                    <Wand2 className="w-3.5 h-3.5" /> Character editor
                                                                </button>
                                                            )}
                                                        </div>
                                                        <ReferenceImageStrip images={photoRefs[char.id] || []}
                                                            onChange={(imgs) => setPhotoRefs(prev => ({ ...prev, [char.id]: imgs }))}
                                                            disabled={generatingPhotoFor === char.id}
                                                            testIdPrefix={`photo-refs-${char.id}`} />
                                                    </div>

                                                    {/* COSTUME & ACCESSORIES — characters only */}
                                                    {showCostume && (
                                                        <div>
                                                            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-widest mb-2">Costume &amp; Accessories</p>
                                                            <div className="aspect-square rounded-2xl border-2 border-dashed border-violet-500/30 bg-violet-500/[0.03] flex items-center justify-center overflow-hidden relative group">
                                                                {(char.costumes?.[0]) ? (
                                                                    <>
                                                                        <img src={char.costumes[0]} alt="Costume" className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                                                        {char.costumes.length > 1 && (getCharacterCostumeForVideo(char) === char.costumes[0] ? (
                                                                            <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold flex items-center gap-1"
                                                                                data-testid={`costume-in-video-badge-${char.id}-0`}>
                                                                                <Check className="w-3 h-3" /> In video
                                                                            </span>
                                                                        ) : (
                                                                            <button type="button" onClick={() => handleSelectCostumeForVideo(char.id, char.costumes[0])}
                                                                                className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/70 hover:bg-emerald-600 text-white text-[10px] font-semibold transition-colors"
                                                                                data-testid={`costume-use-in-video-${char.id}-0`}>
                                                                                Use in video
                                                                            </button>
                                                                        ))}
                                                                        <button type="button" onClick={() => setEditingCostume({ charId: char.id, index: 0, url: char.costumes[0], name: char.name })}
                                                                            className="absolute top-2 right-12 p-2 bg-violet-600 hover:bg-violet-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                                                            title="Edit costume" data-testid={`costume-edit-${char.id}-0`}>
                                                                            <Pencil className="w-4 h-4" />
                                                                        </button>
                                                                        <button type="button" onClick={() => handleRemoveCostume(char.id, 0)}
                                                                            className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                                                            title="Delete costume" data-testid={`costume-delete-${char.id}-0`}>
                                                                            <TrashIcon className="w-4 h-4" />
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <div className="text-center px-4">
                                                                        <div className="w-12 h-16 rounded-lg bg-violet-500/15 border border-violet-500/30 mx-auto mb-3" />
                                                                        <div className="text-sm font-semibold text-violet-200">No costume yet</div>
                                                                        <div className="text-[11px] text-zinc-500 mt-1">Add an outfit or accessories — it&apos;ll preview here</div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {/* Extra costume thumbnails (when more than one) */}
                                                            {char.costumes?.length > 1 && (
                                                                <div className="flex flex-wrap gap-2 mt-2">
                                                                    {char.costumes.slice(1).map((img, idx) => {
                                                                        const inVideo = getCharacterCostumeForVideo(char) === img;
                                                                        return (
                                                                        <div key={idx+1} className="flex flex-col items-center gap-1">
                                                                        <div className={`relative w-12 h-12 rounded-md overflow-hidden border group/cost ${inVideo ? 'border-emerald-500 ring-2 ring-emerald-500/50' : 'border-white/10'}`}>
                                                                            <button type="button" onClick={() => handleSelectCostumeForVideo(char.id, img)}
                                                                                className="w-full h-full" title={inVideo ? 'Used in video' : 'Use this costume in video'}
                                                                                data-testid={`costume-use-in-video-${char.id}-${idx+1}`}>
                                                                                <img src={img} alt={`Costume ${idx+2}`} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                                                            </button>
                                                                            <button type="button" onClick={() => handleRemoveCostume(char.id, idx+1)}
                                                                                className="absolute top-0 right-0 bg-black/70 hover:bg-red-600 rounded-bl w-4 h-4 flex items-center justify-center text-[9px] text-white opacity-0 group-hover/cost:opacity-100 transition-opacity"
                                                                                data-testid={`remove-costume-${char.id}-${idx+1}`}>×</button>
                                                                            <button type="button" onClick={() => setEditingCostume({ charId: char.id, index: idx+1, url: img, name: char.name })}
                                                                                className="absolute bottom-0 left-0 bg-black/70 hover:bg-violet-600 rounded-tr w-4 h-4 flex items-center justify-center text-white opacity-0 group-hover/cost:opacity-100 transition-opacity"
                                                                                title="Edit costume" data-testid={`costume-edit-${char.id}-${idx+1}`}><Pencil className="w-2.5 h-2.5" /></button>
                                                                        </div>
                                                                        {inVideo && (
                                                                            <span className="text-[9px] font-semibold text-emerald-400" data-testid={`costume-in-video-badge-${char.id}-${idx+1}`}>In video</span>
                                                                        )}
                                                                        </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                            {/* Costume action buttons */}
                                                            <input type="file" accept="image/*" className="hidden"
                                                                ref={ref => { costumeFileRefs.current[char.id] = ref; }}
                                                                onChange={e => handleCostumeUpload(e, char.id)} />
                                                            <div className="flex flex-wrap gap-2 mt-3">
                                                                <button type="button" onClick={() => handleGenerateCostume(char)}
                                                                    disabled={generatingCostumeFor === char.id}
                                                                    className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5"
                                                                    data-testid={`costume-generate-${char.id}`}>
                                                                    {generatingCostumeFor === char.id ? (
                                                                        <><div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" /> Generating…</>
                                                                    ) : (
                                                                        <><Wand2 className="w-3.5 h-3.5" /> Generate</>
                                                                    )}
                                                                </button>
                                                                <button type="button" onClick={() => costumeFileRefs.current[char.id]?.click()}
                                                                    className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                                                                    data-testid={`costume-upload-${char.id}`}>
                                                                    <Upload className="w-3.5 h-3.5" /> Upload
                                                                </button>
                                                                <button type="button" onClick={() => setCostumeAssetsCharId(char.id)}
                                                                    className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                                                                    data-testid={`costume-user-assets-${char.id}`}>
                                                                    <FolderOpen className="w-3.5 h-3.5" /> User assets
                                                                </button>
                                                                <button type="button" onClick={() => setCostumeLibraryCharId(char.id)}
                                                                    className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
                                                                    data-testid={`costume-library-${char.id}`}>
                                                                    <Users className="w-3.5 h-3.5" /> Assets library
                                                                </button>
                                                                {char.image && (char.costumes?.length > 0 || char.costumeAndAccessoryImages?.length > 0) && (
                                                                    <button type="button" onClick={() => handleTryOn(char)}
                                                                        disabled={tryOnGenerating === char.id}
                                                                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5"
                                                                        data-testid={`costume-tryon-${char.id}`}>
                                                                        {tryOnGenerating === char.id ? (
                                                                            <><div className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" /> Trying on…</>
                                                                        ) : (
                                                                            <>✨ Try on</>
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <ReferenceImageStrip images={costumeRefs[char.id] || []}
                                                                onChange={(imgs) => setCostumeRefs(prev => ({ ...prev, [char.id]: imgs }))}
                                                                disabled={generatingCostumeFor === char.id}
                                                                testIdPrefix={`costume-refs-${char.id}`} />
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Feb 2026 — Voice (ElevenLabs). Hidden for elements. */}
                                            {!isElement && (
                                                <div className="mt-3 p-3 border border-white/10 rounded-lg bg-black/10" data-testid={`voice-section-${char.id}`}>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">Voice</div>
                                                        {char.voiceId && (
                                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${char.voiceSource === 'cloned' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-violet-500/20 text-violet-300'}`}>
                                                                {char.voiceSource === 'cloned' ? 'Cloned' : 'Library'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {char.voiceId ? (
                                                        <div className="flex items-center justify-between gap-3">
                                                            <div className="min-w-0">
                                                                <div className="text-sm text-zinc-200 truncate" data-testid={`voice-name-${char.id}`}>{char.voiceName || 'ElevenLabs voice'}</div>
                                                                <div className="text-[10px] text-zinc-500 font-mono truncate" title={char.voiceId}>{char.voiceId}</div>
                                                            </div>
                                                            <div className="flex gap-1.5 shrink-0">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setVoiceLibraryCharId(char.id)}
                                                                    className="px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-200 text-xs rounded-lg"
                                                                    data-testid={`voice-change-${char.id}`}
                                                                >
                                                                    Change
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveVoiceInline(char)}
                                                                    className="px-2.5 py-1 bg-red-700/70 hover:bg-red-600 text-white text-xs rounded-lg"
                                                                    data-testid={`voice-remove-${char.id}`}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => setVoiceLibraryCharId(char.id)}
                                                                className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold rounded-lg"
                                                                data-testid={`voice-pick-library-${char.id}`}
                                                            >
                                                                Pick from Library
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setVoiceClonerCharId(char.id)}
                                                                className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg"
                                                                data-testid={`voice-clone-${char.id}`}
                                                            >
                                                                Clone My Voice
                                                            </button>
                                                            <p className="text-[10px] text-zinc-500 w-full">
                                                                Pick from the ElevenLabs library (free) or clone a 15-60s recording (200 credits).
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Remove character — bottom-right */}
                                            <div className="flex justify-end pt-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveCharacter(char.id)}
                                                    className="text-xs text-red-400 hover:text-red-300 font-semibold transition-colors flex items-center gap-1.5"
                                                    data-testid={`remove-character-${char.id}`}
                                                >
                                                    <TrashIcon className="w-3 h-3" />
                                                    Remove {isElement ? 'element' : isNarrator ? 'narrator' : 'character'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Add Character Button */}
                    <button
                        type="button"
                        onClick={handleAddCharacter}
                        className="w-full py-4 border-2 border-dashed border-gray-600 rounded-xl hover:border-purple-500 transition-colors flex items-center justify-center gap-2 text-gray-400 hover:text-purple-400 bg-gray-800/20"
                    >
                        <PlusIcon className="w-6 h-6" />
                        <span className="font-semibold">Add {activeTab === 'characters' ? 'Character' : activeTab === 'elements' ? 'Element' : 'Narrator'} Manually</span>
                    </button>

                    {/* Bottom Action Buttons */}
                    <div className="flex items-center gap-4 mt-8 pt-8 border-t border-gray-700">
                        <button
                            type="button"
                            onClick={onBack}
                            className="px-8 py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-lg flex items-center justify-center transition-colors"
                        >
                            <ArrowLeftIcon className="w-5 h-5 mr-2" />
                            Back
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-8 py-3 bg-violet-600 hover:bg-violet-500 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                            data-testid="cast-world-continue-btn"
                        >
                            Continue
                            <MagicWandIcon className="w-5 h-5" />
                        </button>
                        {onGenerateFullVideo && (
                            <button
                                type="button"
                                onClick={() => onGenerateFullVideo(characters)}
                                className="flex-1 px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-lg"
                            >
                                🎬 Generate Full Video
                            </button>
                        )}
                    </div>
                </form>

                {assetsModalCharId && (
                    <UserAssetsModal
                        activeTab={activeTab === 'elements' ? 'elements' : activeTab === 'narrator' ? 'narrator' : 'characters'}
                        onSelect={(imageUrl, asset) => handleAssetSelect(imageUrl, assetsModalCharId, asset)}
                        onClose={() => setAssetsModalCharId(null)}
                    />
                )}

                {libraryModalCharId && (
                    <ActorsLibraryModal
                        onSelect={(actorData) => handleActorLibrarySelect(actorData, libraryModalCharId)}
                        onClose={() => setLibraryModalCharId(null)}
                    />
                )}

                {costumeAssetsCharId && (
                    <UserAssetsModal
                        activeTab="elements"
                        onSelect={(imageUrl) => handleCostumeAssetSelect(imageUrl, costumeAssetsCharId)}
                        onClose={() => setCostumeAssetsCharId(null)}
                    />
                )}

                {costumeLibraryCharId && (
                    <ActorsLibraryModal
                        onSelect={(actorData) => handleCostumeLibrarySelect(actorData, costumeLibraryCharId)}
                        onClose={() => setCostumeLibraryCharId(null)}
                    />
                )}

                {editingCostume && (
                    <CostumeEditModal
                        imageUrl={editingCostume.url}
                        characterName={editingCostume.name}
                        kind={editingCostume.kind || 'costume'}
                        aspectRatio={editingCostume.kind === 'element' ? (initialData?.aspectRatio || '16:9') : '1:1'}
                        onApply={handleApplyCostumeEdit}
                        onClose={() => setEditingCostume(null)}
                        onTrackCreditsUsed={onTrackCreditsUsed}
                    />
                )}

                {editingCharacter && (
                    <CharacterPortraitModal
                        character={editingCharacter}
                        projectData={initialData}
                        onClose={() => setEditingCharacter(null)}
                        onSave={handleSavePortrait}
                        onDeductCredits={onDeductCredits}
                        onTrackCreditsUsed={onTrackCreditsUsed}
                        onSaveMedia={onSaveMedia}
                    />
                )}

                {/* Feb 2026 — Inline Voice Library picker (opened from card) */}
                {voiceLibraryCharId && (
                    <ElevenLabsVoiceModal
                        onSelect={(voiceId, voiceName) => handleVoicePickedInline(voiceLibraryCharId, voiceId, voiceName)}
                        onClose={() => setVoiceLibraryCharId(null)}
                    />
                )}

                {/* Feb 2026 — Inline Voice Cloner (record / upload → clone) */}
                {voiceClonerCharId && (
                    <VoiceClonerModal
                        voiceNameSuggested={(characters.find(c => c.id === voiceClonerCharId)?.name || '') + ' voice'}
                        onCloned={(payload) => handleVoiceClonedInline(voiceClonerCharId, payload)}
                        onClose={() => setVoiceClonerCharId(null)}
                        onTrackCreditsUsed={onTrackCreditsUsed}
                    />
                )}

                {/* Virtual Try-On Confirmation Modal */}
                {tryOnConfirm && (
                    <TryOnConfirmModal
                        characters={tryOnConfirm.characters}
                        onResolve={tryOnConfirm.resolve}
                    />
                )}

                {/* Image Viewing Modal */}
                {viewingImage && (
                    <div 
                        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                        onClick={() => setViewingImage(null)}
                    >
                        <div 
                            className="relative max-w-4xl max-h-[90vh] bg-gray-900 rounded-lg overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-700">
                                <h3 className="text-lg font-semibold text-white">{viewingImage.name || 'Character Image'}</h3>
                                <button
                                    onClick={() => setViewingImage(null)}
                                    className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                                >
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            {/* Image */}
                            <div className="p-4 flex items-center justify-center">
                                <img 
                                    src={viewingImage.url} 
                                    alt={viewingImage.name || 'Character'}
                                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                                />
                            </div>
                            {/* Footer with download button */}
                            <div className="flex justify-center gap-4 p-4 border-t border-gray-700">
                                <button
                                    onClick={async () => {
                                        const url = viewingImage.url;
                                        const filename = `${(viewingImage.name || 'character').replace(/[^a-zA-Z0-9-_]/g, '_')}-portrait.png`;
                                        try {
                                            // Cross-origin GCS URLs ignore <a download>; fetch as Blob then download
                                            if (url.startsWith('blob:') || url.startsWith('data:')) {
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = filename;
                                                document.body.appendChild(a);
                                                a.click();
                                                document.body.removeChild(a);
                                                return;
                                            }
                                            const r = await fetch(url, { credentials: 'omit' });
                                            if (!r.ok) throw new Error(`HTTP ${r.status}`);
                                            const blob = await r.blob();
                                            const blobUrl = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = blobUrl;
                                            a.download = filename;
                                            document.body.appendChild(a);
                                            a.click();
                                            document.body.removeChild(a);
                                            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                        } catch (err) {
                                            console.error('Download failed:', err);
                                            window.open(url, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                    data-testid="character-image-download-btn"
                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download
                                </button>
                                <button
                                    onClick={() => setViewingImage(null)}
                                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
